import { btn } from "@/components/Page";

const rewardText = (rewards = []) => rewards.map((entry) => `${entry.min}–${entry.max} ${entry.rarity}`).join(" • ");

export const MineLocationCard = ({ mine, isHighestUnlocked, canStart, busy, onStart, startBlockedReason, anchorId }) => {
  const deposit = mine.deposit || {};
  const showBlockedReason = !!startBlockedReason && mine.unlocked && deposit.available && !canStart && !busy;
  const cardClassName = mine.unlocked
    ? "border-amber-700/50 bg-zinc-950/85 shadow-[0_0_0_1px_rgba(180,110,20,0.08)]"
    : "border-amber-900/30 bg-zinc-950/40 opacity-80";
  const highestUnlockedClassName = isHighestUnlocked
    ? "shadow-[0_0_0_1px_rgba(245,158,11,0.55),0_0_28px_rgba(245,158,11,0.22)] ring-1 ring-amber-500/40"
    : "";
  return (
    <article id={anchorId} data-testid={`mining-mine-card-${mine.id}`} className={`border rounded-sm p-3.5 transition-colors scroll-mt-24 ${cardClassName} ${highestUnlockedClassName}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 data-testid={`mining-mine-name-${mine.id}`} className="font-cinzel text-base text-zinc-100 uppercase tracking-wide">{mine.name}</h3>
          <p data-testid={`mining-mine-level-${mine.id}`} className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">Required level {mine.required_level}</p>
        </div>
        <div data-testid={`mining-mine-lock-${mine.id}`} className={`text-xs uppercase tracking-[0.2em] ${mine.unlocked ? "text-amber-300" : "text-zinc-500"}`}>{mine.unlocked ? "Unlocked" : "Locked"}</div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-4 text-xs text-zinc-400">
        <div data-testid={`mining-mine-duration-${mine.id}`}>Duration: <span className="text-zinc-100">{mine.duration_label}</span></div>
        <div data-testid={`mining-mine-energy-${mine.id}`}>Energy: <span className="text-zinc-100">{mine.energy_cost}</span></div>
        <div data-testid={`mining-mine-xp-${mine.id}`}>XP: <span className="text-zinc-100">{mine.xp_min}–{mine.xp_max}</span></div>
        <div data-testid={`mining-mine-risk-${mine.id}`}>Risk: <span className="text-zinc-100">{mine.risk_label}</span></div>
      </div>

      <div data-testid={`mining-mine-deposit-${mine.id}`} className="mt-4 border border-zinc-800 bg-zinc-900/50 p-3 rounded-sm">
        <div className="flex items-center justify-between gap-3 text-xs uppercase tracking-[0.2em] text-zinc-500">
          <span>Deposit</span>
          <span className={deposit.status === "exhausted" ? "text-amber-400" : deposit.status === "low" ? "text-amber-300" : "text-zinc-300"}>
            {deposit.status || "stable"}
          </span>
        </div>
        <div className="mt-2 text-sm text-zinc-300">
          <span className="text-zinc-100">{deposit.current_units ?? 0}</span> / {deposit.capacity ?? 0} vein units
        </div>
        <div className="mt-1 text-xs text-zinc-500">
          Costs {deposit.cost_per_session ?? 0} units per run • Restores {deposit.restore_per_hour ?? 0}/h
        </div>
        {deposit.respawn_at && (
          <div data-testid={`mining-mine-respawn-${mine.id}`} className="mt-1 text-xs text-amber-400">
            Exhausted until {new Date(deposit.respawn_at).toLocaleString()}
          </div>
        )}
      </div>

      <div data-testid={`mining-mine-rewards-${mine.id}`} className="mt-4 text-xs text-zinc-300">Expected rewards: {mine.ore_rush_active ? <span data-testid={`mining-mine-ore-rush-rewards-${mine.id}`}><span className="text-zinc-500 line-through">{rewardText(mine.base_expected_ores)}</span> <span className="mx-1 text-zinc-500">→</span><span className="font-semibold text-amber-200">{rewardText(mine.expected_ores)}</span></span> : rewardText(mine.expected_ores)}</div>
      <div data-testid={`mining-mine-great-yield-${mine.id}`} className="mt-1 text-xs uppercase tracking-[0.2em] text-zinc-500">Great yield chance: {(mine.great_yield_chance * 100).toFixed(2)}%</div>
      <div data-testid={`mining-mine-discovery-${mine.id}`} className="mt-1 text-xs uppercase tracking-[0.2em] text-zinc-500">Discovery chance: {mine.ore_rush_active ? <><span className="line-through">{(mine.base_rare_discovery_chance * 100).toFixed(2)}%</span> <span className="text-amber-200">→ {(mine.rare_discovery_chance * 100).toFixed(2)}%</span></> : `${(mine.rare_discovery_chance * 100).toFixed(2)}%`}</div>
      {!!mine.possible_discoveries?.length && (
        <div data-testid={`mining-mine-discovery-list-${mine.id}`} className="mt-3 flex flex-wrap gap-2">
          {mine.possible_discoveries.map((discovery) => (
            <span key={discovery.id} className="border border-amber-900/30 bg-amber-950/10 px-2 py-1 text-[11px] uppercase tracking-[0.18em] text-amber-300">
              {discovery.name}
            </span>
          ))}
        </div>
      )}

      <button
        type="button"
        data-testid={`mining-start-button-${mine.id}`}
        disabled={!canStart || !mine.unlocked || busy || !deposit.available}
        onClick={() => onStart(mine.id)}
        className={`${btn()} mt-4 w-full justify-center`}
      >
        {!mine.unlocked ? `Unlocks at ${mine.required_level}` : !deposit.available ? "Deposit Unavailable" : (busy ? "Starting Mining" : "Start Mining")}
      </button>
      {showBlockedReason && (
        <div data-testid={`mining-start-blocked-reason-${mine.id}`} className="mt-2 text-xs text-amber-400">
          {startBlockedReason}
        </div>
      )}
    </article>
  );
};